// The HTTP boundary for the connection-lifecycle writes New-Front shipped after v4's Connections
// port (contract 03 §15). Every route here is admin-gated or answers a refusal the screen owns, so
// none of them may raise the global toast: the caller's service maps the `ApiError`.
//
// Envelopes differ per route and are read as the backend sends them: release and deletion answer
// `{ status, data }`, the grant listing has been seen both enveloped and flat, the checkpoint verify
// answers nothing the screen reads, the byte limit always envelopes, and the Singular summary is
// flat while its budget edit envelopes.
import type { AxiosRequestConfig } from 'axios'

import type {
  OAuthGrantFamilyDto,
  QueryByteLimitDto,
  SingularConnectionDto,
  SingularSummaryDto,
} from '../models/connectionLifecycle.types'

import { appHttp, unwrap, unwrapEnvelope, type ApiEnvelope } from '@/shared/http'

const FEATURE_OWNED_ERRORS: AxiosRequestConfig = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
}

type Id = number | string

const queryByteLimitPath = (workspaceId: Id): string =>
  `/integrations/${workspaceId}/big-query-integration/query-byte-limit/`
const singularPath = (workspaceId: Id): string =>
  `/integrations/${workspaceId}/singular-integration/`

export class ConnectionLifecycleApi {
  /**
   * The account's stored OAuth grants, grouped by release family (ORP-24028). Read so the release
   * confirmation names the identity it removes rather than "this connection".
   */
  static async listOAuthGrants(workspaceId: Id): Promise<OAuthGrantFamilyDto[]> {
    const response = await appHttp.get<
      { families?: OAuthGrantFamilyDto[] } | ApiEnvelope<{ families?: OAuthGrantFamilyDto[] }>
    >(`/integrations/${workspaceId}/oauth-grants/`, FEATURE_OWNED_ERRORS)
    const body = response.data as {
      families?: OAuthGrantFamilyDto[]
      data?: { families?: OAuthGrantFamilyDto[] }
    }
    return body?.data?.families ?? body?.families ?? []
  }

  /**
   * Release one stored grant. `familyKey` is the path segment (`google`, `meta`, …); the id is only
   * sent when the caller read a single grant for the family — see `OAuthGrantSummary`.
   */
  static async releaseOAuthGrant(
    workspaceId: Id,
    familyKey: string,
    oauthInformationId: Id | null,
  ): Promise<void> {
    await appHttp.post(
      `/integrations/${workspaceId}/${familyKey}/release/`,
      oauthInformationId === null ? {} : { oauth_information_id: oauthInformationId },
      FEATURE_OWNED_ERRORS,
    )
  }

  /**
   * Ask Orphex to delete the data collected for these connections (ORP-24747). A REQUEST, not the
   * deletion: the backend moves the rows to `deletion_requested`, which stops their collection, and
   * the team clears the data by hand. Selected by `WorkspaceIntegrationAccount` id so the request
   * never reaches further than the card that sent it names.
   */
  static async requestDeletion(workspaceId: Id, accountIds: readonly Id[]): Promise<void> {
    await appHttp.post(
      `/integrations/${workspaceId}/request-integration-deletion/`,
      { account_ids: accountIds.map(Number) },
      FEATURE_OWNED_ERRORS,
    )
  }

  /**
   * "I've completed it" — re-check the stored Meta token now rather than at the next hourly sweep.
   * The Health Checks card carries its own `action_url` for the same endpoint, so the path is an
   * input; the banner passes the canonical one.
   */
  static async verifyMetaCheckpoint(path: string): Promise<void> {
    await appHttp.post(path, null, FEATURE_OWNED_ERRORS)
  }

  /** `null` from a workspace whose response carried no summary. A 404 means "no BigQuery". */
  static async getQueryByteLimit(workspaceId: Id): Promise<QueryByteLimitDto | null> {
    const response = await appHttp.get<ApiEnvelope<QueryByteLimitDto | null>>(
      queryByteLimitPath(workspaceId),
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope(response) ?? null
  }

  /** `queryByteLimit: null` removes the limit. Resolves to the updated summary. */
  static async updateQueryByteLimit(
    workspaceId: Id,
    queryByteLimit: number | null,
  ): Promise<QueryByteLimitDto | null> {
    const response = await appHttp.patch<ApiEnvelope<QueryByteLimitDto | null>>(
      queryByteLimitPath(workspaceId),
      { query_byte_limit: queryByteLimit },
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope(response) ?? null
  }

  /** The Singular summary is read flat — New-Front reads `response.data` as the summary itself. */
  static async getSingularSummary(workspaceId: Id): Promise<SingularSummaryDto | null> {
    const response = await appHttp.get<SingularSummaryDto | null>(
      singularPath(workspaceId),
      FEATURE_OWNED_ERRORS,
    )
    return unwrap(response) ?? null
  }

  /**
   * Set one connection's daily row budget without a reconnect (ORP-25841). `dailyRowBudget: null`
   * returns it to the default. Resolves to the updated row.
   */
  static async updateSingularDailyRowBudget(
    workspaceId: Id,
    dailyRowBudget: number | null,
    integrationAccountId: Id | null,
  ): Promise<SingularConnectionDto | null> {
    const response = await appHttp.patch<ApiEnvelope<SingularConnectionDto | null>>(
      `${singularPath(workspaceId)}daily-row-budget/`,
      integrationAccountId === null
        ? { daily_row_budget: dailyRowBudget }
        : { daily_row_budget: dailyRowBudget, integration_account_id: integrationAccountId },
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope(response) ?? null
  }
}

/** The canonical verify route; the Health Checks card may hand a different one. */
export function metaCheckpointVerifyPath(workspaceId: Id): string {
  return `/integrations/${workspaceId}/meta/checkpoint/verify/`
}
